import "../css/header.scss";
import React, { useContext, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CartContext } from "./CartContext";
import {
  FiHeart,
  FiMenu,
  FiSearch,
  FiShoppingBag,
  FiUser,
  FiX,
} from "react-icons/fi";
import siteConfig from "../siteConfig";

function Header() {
  const { cartItems } = useContext(CartContext);
  const { wishlist } = useContext(CartContext);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [moreOpen, setMoreOpen] = useState(false);
  const navigate = useNavigate();

  const cartCount = cartItems.reduce((total, item) => total + item.quantity, 0);

  const toTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const closeMenu = () => {
    setMenuOpen(false);
    toTop();
  };

  const submitSearch = (event) => {
    event.preventDefault();
    navigate(
      `/shop${
        query.trim() ? `?search=${encodeURIComponent(query.trim())}` : ""
      }`
    );
    setSearchOpen(false);
    toTop();
  };

  const navItems = [
    ["Shop All", "/shop"],
    ["Kitchen", "/shop?category=Kitchen"],
    ["Drinkware", "/shop?category=Drinkware"],
    ["Workspace", "/shop?category=Workspace"],
    ["Accessories", "/shop?category=Accessories"],
    ["Home Care", "/shop?category=Home%20Care"],
    ["Home Accents", "/shop?category=Home%20Accents"],
    ["Sale", "/shop?sort=price-low"],
  ];

  return (
    <header className="site-header">
      <div className="announcement" aria-label="Store announcement">
        {siteConfig.tagline.split("").map((letter, index) => (
          <span
            key={`${letter}-${index}`}
            style={{ animationDelay: `${index * 45}ms` }}
          >
            {letter === " " ? "\u00a0" : letter}
          </span>
        ))}
      </div>

      <div className="header-main">
        <button
          className="header-icon mobile-only"
          onClick={() => setMenuOpen(true)}
          aria-label="Open menu"
        >
          <FiMenu />
        </button>
        <button
          className="header-icon"
          onClick={() => setSearchOpen((open) => !open)}
          aria-label="Search"
        >
          <FiSearch />
        </button>
        <Link
          className="header-logo"
          to="/"
          onClick={toTop}
          aria-label={`${siteConfig.brandName} home`}
        >
          <img src={siteConfig.logo} alt="" aria-hidden="true" />
        </Link>
        <div className="header-actions">
          <Link
            className="header-icon desktop-only"
            to="/about"
            aria-label="Account"
          >
            <FiUser />
          </Link>
          <Link
            className="header-icon desktop-only icon-with-count"
            to="/shop?wishlist=true"
            aria-label="Wishlist"
          >
            <FiHeart />
            {wishlist.length > 0 && <span>{wishlist.length}</span>}
          </Link>
          <Link
            className="header-icon icon-with-count"
            to="/cartpage"
            aria-label={`Cart with ${cartCount} items`}
          >
            <FiShoppingBag />
            {cartCount > 0 && <span>{cartCount}</span>}
          </Link>
        </div>
      </div>

      {searchOpen && (
        <form className="header-search" onSubmit={submitSearch}>
          <FiSearch aria-hidden="true" />
          <input
            autoFocus
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search our colorful goodies"
            aria-label="Search products"
          />
          <button
            type="button"
            onClick={() => setSearchOpen(false)}
            aria-label="Close search"
          >
            <FiX />
          </button>
        </form>
      )}

      <nav className="desktop-nav" aria-label="Main navigation">
        {navItems.map(([label, path]) => (
          <Link
            key={label}
            to={path}
            onClick={toTop}
            className={label === "Sale" ? "sale-link" : ""}
          >
            {label}
          </Link>
        ))}
        <div className="more-menu">
          <button
            type="button"
            aria-expanded={moreOpen}
            onClick={() => setMoreOpen((open) => !open)}
          >
            More
          </button>
          {moreOpen && (
            <div className="more-menu__panel">
              <Link
                to="/privacy"
                onClick={() => {
                  setMoreOpen(false);
                  toTop();
                }}
              >
                Privacy
              </Link>
              <Link
                to="/terms"
                onClick={() => {
                  setMoreOpen(false);
                  toTop();
                }}
              >
                Terms
              </Link>
              <Link
                to="/cookies"
                onClick={() => {
                  setMoreOpen(false);
                  toTop();
                }}
              >
                Cookies
              </Link>
              <Link
                to="/faqs"
                onClick={() => {
                  setMoreOpen(false);
                  toTop();
                }}
              >
                FAQs
              </Link>
              <Link to="/accessibility" onClick={() => { setMoreOpen(false); toTop(); }}>
                Accessibility
              </Link>
            </div>
          )}
        </div>
      </nav>

      <div
        className={`mobile-menu ${menuOpen ? "is-open" : ""}`}
        aria-hidden={!menuOpen}
      >
        <button
          className="mobile-menu-close"
          onClick={() => setMenuOpen(false)}
          aria-label="Close menu"
        >
          <FiX />
        </button>
        <span className="mobile-menu-logo" aria-hidden="true">
          <img src={siteConfig.logo} alt="" />
        </span>
        <nav>
          {navItems.map(([label, path]) => (
            <Link key={label} to={path} onClick={closeMenu}>
              {label}
              <span>→</span>
            </Link>
          ))}
        </nav>
        <Link to="/about" onClick={closeMenu}>
          About
        </Link>
        <Link to="/contact" onClick={closeMenu}>
          Contact
        </Link>
        <Link to="/privacy" onClick={closeMenu}>
          Privacy
        </Link>
        <Link to="/terms" onClick={closeMenu}>
          Terms
        </Link>
        <Link to="/cookies" onClick={closeMenu}>
          Cookies
        </Link>
        <Link to="/faqs" onClick={closeMenu}>
          FAQs
        </Link>
        <Link to="/accessibility" onClick={closeMenu}>
          Accessibility
        </Link>
      </div>
      {menuOpen && (
        <button
          className="menu-backdrop"
          onClick={() => setMenuOpen(false)}
          aria-label="Close menu"
        />
      )}
    </header>
  );
}

export default Header;
